import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '../innovher/PageHeader';
import { EditorialIntro } from '../innovher/SplitSection';
import SEO from '../SEO';
import { STOCK } from '../../lib/images';

const SIGNALS = [
  { code: '01', label: '6-month immersion' },
  { code: '02', label: 'Gen Z' },
  { code: '03', label: 'Family business' },
  { code: '04', label: 'Venture exposure' },
  { code: '05', label: 'Investor mindset' },
  { code: '06', label: 'Innovation' },
];

const PATHWAYS = [
  {
    code: '01',
    title: 'Enter the family enterprise',
    text: 'Return with an innovation lens, ready to imagine what the next chapter of the business can become.',
  },
  {
    code: '02',
    title: 'Build something of your own',
    text: 'Use continuous exposure to founders, mentors and real ventures to shape and start your own path.',
  },
  {
    code: '03',
    title: 'Participate as an informed investor',
    text: 'Develop the perspective to engage with the venture economy with clarity, curiosity and judgment.',
  },
];

const INSIDE = [
  {
    title: 'Continuous exposure',
    text: 'Stay close to founders, investors, mentors, technologies and live ventures inside the InnovHer ecosystem.',
  },
  {
    title: 'Venture interactions',
    text: 'Meet real companies and see how ideas move from ambition to market reality.',
  },
  {
    title: 'Learning sessions',
    text: 'Build fluency across startups, innovation and venture investing through focused sessions.',
  },
  {
    title: 'Hands-on experiences',
    text: 'Learn by being present in the work, not by sitting through a traditional classroom programme.',
  },
];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

export default function InnovZPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        path="/innovz"
        title="InnovZ | From Inheriting Enterprise to Imagining What Comes Next"
        description="InnovZ is a six-month immersive experience for Gen Z and next-generation business families, a runway into startups, innovation and venture investing inside InnovHer."
        keywords="InnovZ, Gen Z entrepreneurship, family business next generation, venture exposure, investor mindset, InnovHer"
      />

      <PageHeader
        eyebrow="Leadership & talent layer"
        title={
          <>
            InnovZ
            <span className="block mt-3 sm:mt-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-white/85 tracking-tight leading-[1.15] max-w-4xl">
              From inheriting enterprise to imagining what comes{' '}
              <span className="text-[#ff8ec5]">next</span>
            </span>
          </>
        }
        subtitle="A six-month immersive experience for Gen Z and emerging second- and third-generation entrepreneurs."
        image={STOCK.innovz}
        breadcrumbs={[
          { label: 'Infrastructure', to: '/#infrastructure' },
          { label: 'InnovZ' },
        ]}
      />

      {/* Intro */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="The experience"
            title="A runway into the venture economy"
            lead="InnovZ introduces the next generation of business families to the world of startups, innovation and venture investing."
          >
            <p>
              Designed as a six-month immersive experience for Gen Z and emerging second- and
              third-generation entrepreneurs, InnovZ places participants inside the InnovHer
              ecosystem, alongside founders, investors, mentors, technologies and real ventures.
            </p>
            <p>
              Through continuous exposure, venture interactions, learning sessions and hands-on
              experiences, participants begin developing the perspective to eventually enter a
              family enterprise with an innovation lens, build something of their own, or
              participate in the ecosystem as an informed investor.
            </p>
          </EditorialIntro>

          <div className="reveal-up mt-14 sm:mt-16 rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#d42b92]/18 via-white/[0.025] to-transparent p-8 sm:p-10 lg:p-12 max-w-4xl">
            <p className="text-[#ff8ec5] text-[11px] tracking-[0.28em] uppercase mb-4">
              Positioning
            </p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white leading-[1.15]">
              Not a classroom program.
              <span className="block mt-2 text-white/80 font-medium">
                A runway into the venture economy.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Signals */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/3 right-0 w-[380px] h-[380px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up max-w-3xl">
            <SectionEyebrow>What defines InnovZ</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              Built around immersion, not{' '}
              <span className="text-[#d42b92]">instruction alone</span>
            </h2>
          </div>

          <div className="reveal-up mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
            {SIGNALS.map((s) => (
              <div key={s.label} className="bg-[#08080a] px-6 py-7 sm:px-7 sm:py-8">
                <div className="text-[#ff8ec5] text-[11px] tracking-[0.22em] font-semibold mb-3">
                  {s.code}
                </div>
                <p className="text-white text-lg sm:text-xl font-medium tracking-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inside the ecosystem */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up max-w-3xl">
            <SectionEyebrow>Inside the InnovHer ecosystem</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              Learn beside the people who are{' '}
              <span className="text-[#d42b92]">already building</span>
            </h2>
            <p className="mt-5 text-white/55 text-base sm:text-lg leading-relaxed max-w-2xl">
              Participants spend six months close to founders, investors, mentors, technologies and
              real ventures, developing perspective through proximity and practice.
            </p>
          </div>

          <div className="reveal-up mt-12 space-y-0 border-t border-white/10">
            {INSIDE.map((item, i) => (
              <article
                key={item.title}
                className="grid lg:grid-cols-12 gap-4 lg:gap-10 py-8 sm:py-10 border-b border-white/10"
              >
                <div className="lg:col-span-1 text-[#ff8ec5] text-[11px] tracking-[0.22em] font-semibold pt-1">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="lg:col-span-4 text-xl sm:text-2xl font-semibold text-white tracking-tight">
                  {item.title}
                </h3>
                <p className="lg:col-span-7 text-white/55 text-[15px] sm:text-base leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up max-w-3xl">
            <SectionEyebrow>Where it can lead</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              Three paths. One sharper{' '}
              <span className="text-[#d42b92]">perspective</span>
            </h2>
            <p className="mt-5 text-white/55 text-base sm:text-lg leading-relaxed max-w-2xl">
              InnovZ helps participants begin developing the perspective to choose what comes next
              with more clarity.
            </p>
          </div>

          <div className="reveal-up mt-12 grid md:grid-cols-3 gap-4 sm:gap-5">
            {PATHWAYS.map((p) => (
              <div
                key={p.title}
                className="rounded-[1.5rem] border border-white/10 bg-white/[0.02] p-6 sm:p-8 hover:border-[#d42b92]/45 transition-colors"
              >
                <div className="text-[#ff8ec5] text-[11px] tracking-[0.22em] font-semibold mb-5">
                  {p.code}
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight leading-snug">
                  {p.title}
                </h3>
                <p className="mt-4 text-white/55 text-sm sm:text-base leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#d42b92]/20 via-white/[0.03] to-transparent p-8 sm:p-12 lg:p-16 max-w-4xl">
            <div className="absolute -right-20 -bottom-20 h-64 w-64 rounded-full bg-[#d42b92]/25 blur-3xl pointer-events-none" />
            <div className="relative">
              <SectionEyebrow>Join InnovZ</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
                Step into the venture economy
              </h2>
              <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed max-w-2xl">
                For Gen Z and emerging second- and third-generation entrepreneurs ready to move from
                inheriting enterprise to imagining what comes next.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#d42b92] px-6 py-3 text-sm font-medium text-white hover:bg-[#ff8ec5] hover:text-black transition-colors"
              >
                Explore InnovZ <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
